import { InformationCircleIcon } from "@heroicons/react/24/solid";
import { useState, useRef, ReactNode, useEffect, useCallback } from "react";

interface InfoTooltipProps {
    children: ReactNode;
    trigger?: ReactNode;
}

export function InfoTooltip({ children, trigger }: InfoTooltipProps) {
    const [isVisible, setIsVisible] = useState(false);
    const [position, setPosition] = useState<{ top: number; left: number }>({
        top: -9999,
        left: -9999,
    });
    const triggerRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);

    const calculatePosition = useCallback(() => {
        if (!triggerRef.current || !tooltipRef.current) return;

        const triggerRect = triggerRef.current.getBoundingClientRect();
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        const tooltipWidth = tooltipRect.width || 320;
        const tooltipHeight = tooltipRect.height || 150;
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        const centeredLeft =
            triggerRect.left + triggerRect.width / 2 - tooltipWidth / 2;
        const clampedLeft = Math.max(
            8,
            Math.min(centeredLeft, viewportWidth - tooltipWidth - 8),
        );

        const defaultTop = triggerRect.bottom + 8;
        const wouldOverflowBottom = defaultTop + tooltipHeight > viewportHeight;
        const finalTop = wouldOverflowBottom
            ? triggerRect.top - tooltipHeight - 8
            : defaultTop;

        setPosition({ top: finalTop, left: clampedLeft });
    }, []);

    useEffect(() => {
        if (!isVisible) return;

        const frameId = requestAnimationFrame(() => {
            calculatePosition();
        });

        return () => {
            cancelAnimationFrame(frameId);
        };
    }, [isVisible, calculatePosition]);

    const handleMouseEnter = useCallback(() => {
        setIsVisible(true);
    }, []);

    const handleMouseLeave = useCallback(() => {
        setIsVisible(false);
    }, []);

    return (
        <div
            ref={triggerRef}
            className="tt-trigger group"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            {trigger || (
                <InformationCircleIcon className="size-4 fill-gray-500" />
            )}
            {isVisible && (
                <div
                    ref={tooltipRef}
                    className="tt-container !fixed z-[1000]"
                    style={{
                        top: `${position.top}px`,
                        left: `${position.left}px`,
                    }}
                >
                    <div className="tt w-80">{children}</div>
                </div>
            )}
        </div>
    );
}
